import { useContext, useEffect, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import dayjs from 'dayjs';
import { deferStampDataTestIdOnDescendantRef, toTestIdSlug } from '@utils/testUtils';
import clsx from 'clsx';

const VALID_TIMEZONE_REGIONS = ['Africa/', 'America/', 'Antarctica/', 'Arctic/', 'Asia/', 'Atlantic/', 'Australia/', 'Europe/', 'Indian/', 'Pacific/', 'UTC'];

const toTimezoneOptions = (data: string[]): { name: string; code: string }[] =>
  data.filter(tz => VALID_TIMEZONE_REGIONS.some(region => tz.startsWith(region))).map(tz => ({ name: tz, code: tz }));

// Native IANA list from the browser (ES2022 Intl.supportedValuesOf, not yet in our TS lib target).
// Used on unauthenticated pages where the getAvailableTimezones API call would 401.
const getBrowserTimezones = (): string[] => {
  const intl = Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] };
  if (typeof intl.supportedValuesOf === 'function') {
    try {
      return intl.supportedValuesOf('timeZone');
    } catch {
      // fall through to the guessed single zone below
    }
  }
  const guessed = dayjs.tz.guess();
  return guessed ? [guessed] : [];
};

interface InputTimezoneProps {
  value?: string;
  onChange?: (timezone: string) => void;
  disabled?: boolean;
  code?: string;
  dataTestId?: string;
  label?: string;
  classNameWrapper?: string;
  // Source the list from the browser (Intl) instead of the authenticated API. Required on public pages.
  useBrowserTimezones?: boolean;
}

const InputTimezone: React.FC<InputTimezoneProps> = ({
  value = dayjs.tz.guess(),
  onChange,
  disabled = false,
  code,
  dataTestId,
  label,
  classNameWrapper,
  useBrowserTimezones = false,
}) => {
  const { tr } = useTranslationLgs();
  const { getAvailableTimezones } = useContext(ApiContext);
  const [timezones, setTimezones] = useState<{name: string, code: string}[]>([]);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (useBrowserTimezones) {
      setTimezones(toTimezoneOptions(getBrowserTimezones()));
      return;
    }
    getAvailableTimezones(
      (data) => {
        setTimezones(toTimezoneOptions(data));
      },
      code
    );
  }, [code, getAvailableTimezones, useBrowserTimezones]);

  useEffect(() => {
    return deferStampDataTestIdOnDescendantRef(wrapperRef, '.p-dropdown', dataTestId);
  }, [dataTestId, timezones]);

  return (
    <div ref={wrapperRef} className={classNameWrapper}>
      {label && <label>{label}</label>}
      <Dropdown
        value={value}
        options={timezones}
        onChange={(e) => onChange?.(e.value)}
        optionLabel="name"
        optionValue="code"
        itemTemplate={(option: { name: string; code: string }) => (
          <span data-testid={dataTestId ? `${dataTestId}-${toTestIdSlug(option.code)}` : undefined}>
            {option.name}
          </span>
        )}
        placeholder={tr(`selectATimezone`, `Select a timezone`)}
        disabled={disabled}
        filter
        filterBy="name"
        className="w-full"
        panelClassName={dataTestId}
      />
    </div>
  );
};

export default InputTimezone;
